import styled from 'styled-components';
import { BigNumber, BigNumberish } from 'ethers';
import { TFunction } from 'i18next';

import { ButtonS } from '../Button';
import {
  TextValue,
  CondensedRowContainer,
  CondensedValuesContainer,
  CondensedValue,
} from '../Table/components';
import { Label3 } from '../Text';
import { formatDollarValue } from '../../utils/general';
import { Market } from '../../contexts/MarketsContext';

export const CloseButton = styled(ButtonS)`
  &:disabled {
    background: none;
    border-color: rgba(0, 0, 0, 0);

    &:disabled {
      box-shadow: none;
    }

    &:enabled:active {
      border-color: rgba(0, 0, 0, 0);
      box-shadow: none;
    }
  }
`;

export interface ActivityInstance {
  id: string;
  action: string;
  positionSize?: BigNumberish;
  openNotional?: BigNumberish;
  market?: Market;
  price?: BigNumberish;
  pnl?: BigNumberish;
  fees?: BigNumberish;
  timestamp: number;
}

export interface Row {
  id: string;
  action: React.ReactNode;
  market: React.ReactNode;
  size: React.ReactNode;
  notional: React.ReactNode;
  price: React.ReactNode;
  fees: React.ReactNode;
  timestamp: React.ReactNode;
}

export const headers = (t: TFunction): Row => ({
  id: 'header',
  action: t('Action'),
  market: t('Market'),
  size: t('Size'),
  notional: t('Notional'),
  price: t('Price'),
  fees: t('Fees'),
  timestamp: t('Timestamp (UTC)'),
});

export const renderRow = (activity: ActivityInstance, t: TFunction): Row => ({
  id: activity.id,
  action: <TextValue>{t(activity.action)}</TextValue>,
  market: <TextValue>{activity.market?.name}</TextValue>,
  size: (
    <TextValue value={activity.positionSize ? 0 : null}>
      {activity.positionSize
        ? `${formatDollarValue(
            BigNumber.from(activity.positionSize).abs(),
            18,
            true,
            activity.market?.baseDecimals ?? 2,
          )} ${activity.market?.base}`
        : '-'}
    </TextValue>
  ),
  notional: (
    <TextValue value={activity.openNotional ? 0 : null}>
      {activity.openNotional
        ? formatDollarValue(BigNumber.from(activity.openNotional).abs(), 18, false, 2)
        : '-'}
    </TextValue>
  ),
  price: (
    <TextValue value={activity.price ? 0 : null}>
      {activity.price
        ? formatDollarValue(activity.price, 18, false, activity.market?.displayDecimals ?? 2)
        : '-'}
    </TextValue>
  ),
  fees: (
    <TextValue value={activity.fees ? -Number(activity.fees.toString()) : null}>
      {activity.fees ? formatDollarValue(BigNumber.from(activity.fees).mul(-1), 18, false, 2) : '-'}
    </TextValue>
  ),
  timestamp: (
    <TextValue>
      {new Date(activity.timestamp).toISOString().substring(0, 19).replace('T', ' ')}
    </TextValue>
  ),
});

export const renderCondensedRow = (activity: ActivityInstance, t: TFunction) => (
  <CondensedRowContainer key={activity.id}>
    <CondensedValuesContainer>
      <CondensedValue>
        <Label3>{headers(t).action}</Label3>
        <TextValue>{t(activity.action)}</TextValue>
      </CondensedValue>
      <CondensedValue>
        <Label3>{headers(t).market}</Label3>
        <TextValue>{activity.market?.name}</TextValue>
      </CondensedValue>
      <CondensedValue>
        <Label3>{headers(t).size}</Label3>
        <TextValue value={activity.positionSize ? 0 : null}>
          {activity.positionSize
            ? `${formatDollarValue(
                BigNumber.from(activity.positionSize).abs(),
                18,
                true,
                activity.market?.baseDecimals ?? 2,
              )} ${activity.market?.base}`
            : '-'}
        </TextValue>
      </CondensedValue>
      <CondensedValue>
        <Label3>{headers(t).notional}</Label3>
        <TextValue value={activity.openNotional ? 0 : null}>
          {activity.openNotional
            ? formatDollarValue(BigNumber.from(activity.openNotional).mul(-1), 18, false, 2)
            : '-'}
        </TextValue>
      </CondensedValue>
      <CondensedValue>
        <Label3>{headers(t).price}</Label3>
        <TextValue value={activity.price ? 0 : null}>
          {activity.price
            ? formatDollarValue(activity.price, 18, false, activity.market?.displayDecimals ?? 2)
            : '-'}
        </TextValue>
      </CondensedValue>
      <CondensedValue>
        <Label3>{headers(t).fees}</Label3>
        <TextValue value={activity.fees ? -Number(activity.fees.toString()) : null}>
          {activity.fees
            ? formatDollarValue(BigNumber.from(activity.fees).mul(-1), 18, false, 2)
            : '-'}
        </TextValue>
      </CondensedValue>
      <CondensedValue>
        <Label3>{headers(t).timestamp}</Label3>
        <TextValue style={{ textAlign: 'right' }}>
          {new Date(activity.timestamp).toISOString().substring(0, 19).replace('T', ' ')}
        </TextValue>
      </CondensedValue>
    </CondensedValuesContainer>
  </CondensedRowContainer>
);
